import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Typography, Box, TextField, Button,
  CircularProgress, alpha, IconButton,
  Switch, Paper, Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  PhotoLibrary as GalleryIcon, Event as EventIcon,
  Public as PublicIcon, EmojiEvents as AchievementIcon,
  CloudUpload as UploadIcon, Delete as DeleteIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Close as CloseIcon, Image as ImageIcon, Star as StarIcon,
  Lock as LockIcon, Visibility as VisibilityIcon,
  Description as DescriptionIcon, CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import ConfirmUpdateDialog from '../../common/ConfirmDeleteDialog';
import { formatFieldErrorsMessage, rejectConfirmValidation } from '../../../utils/formValidationFeedback';
import CommonSectionCard from '../../common/SectionCard';
import SubmitActionsCard from '../../common/SubmitActionsCard';
import FormDraftNotice from '../../common/FormDraftNotice';
import useFormDraftBindings from '../../../hooks/useFormDraftBindings';
import SearchableSelect from '../../common/SearchableSelect';
import { ACCENT, BG_BOTTOM, BG_TOP, CARD, SUCCESS, TEXT_SECONDARY } from '../../common/SaaSTokens';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canManageGallery } from '../../../utils/galleryPermissions';
import { GALLERY_PUBLICITY_FORM_OPTIONS } from '../../../constants/galleryPublicity';
import StorageImage from '../../common/StorageImage';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const B = { 700: '#1D4ED8' };
const NEON        = ACCENT;
const NEON2       = TEXT_SECONDARY;
const NEON3       = SUCCESS;
const SURFACE_BASE = BG_BOTTOM;
const SURFACE_MID  = BG_TOP;
const SURFACE_UP   = CARD;
const IMAGE_PANEL_HEIGHT = 240;

const PUBLICITY_OPTION_ICONS = {
  public: PublicIcon,
  private_view: VisibilityIcon,
  private_edit: LockIcon,
  private_access: AddPhotoIcon,
};

const PUBLICITY_OPTION_COLORS = {
  public: NEON3,
  private_view: NEON,
  private_edit: NEON2,
  private_access: '#F59E0B',
};

const GALLERY_CSS = `
  @keyframes form-glow-in { from { box-shadow: 0 0 0 0 rgba(56,189,248,0.45); } to { box-shadow: 0 0 0 4px rgba(56,189,248,0); } }
  .MuiFormLabel-asterisk { color: #EF4444 !important; }
`;

const MotionBox   = motion(Box);
const MotionPaper = motion(Paper);

const labelSx = (color = alpha(NEON, 0.55)) => ({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: 1.6,
  textTransform: 'uppercase',
  color,
  lineHeight: 1,
  mb: 1,
  display: 'block',
});

// ─── SectionCard wrapper ───────────────────────────────────────────────────────
const SectionCard = ({ title, icon, delay = 0, children }) => (
  <CommonSectionCard title={title} icon={icon} delay={delay}>
    {children}
  </CommonSectionCard>
);

// ─── StyledField ───────────────────────────────────────────────────────────────
const StyledField = ({ sx, ...props }) => (
  <TextField
    {...props}
    sx={{
      '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.9rem',
        color: alpha('#E2E8F0', 0.85),
        background: alpha(SURFACE_UP, 0.7),
        backdropFilter: 'blur(8px)',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '& fieldset': { borderColor: alpha(NEON, 0.18), transition: 'border-color 0.2s ease' },
        '&:hover fieldset': { borderColor: alpha(NEON, 0.4) },
        '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(NEON, 0.12)}`, animation: 'form-glow-in 0.3s ease' },
        '&.Mui-focused fieldset': { borderColor: alpha(NEON, 0.65), borderWidth: '1.5px' },
        '&.Mui-disabled': { opacity: 0.45 },
      },
      '& .MuiInputBase-input': {
        color: alpha('#E2E8F0', 0.85),
        '&::placeholder': { color: alpha(NEON, 0.3), opacity: 1 },
        '&.Mui-disabled': { WebkitTextFillColor: alpha('#E2E8F0', 0.35) },
      },
      '& .MuiInputLabel-root': { fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', color: alpha(NEON, 0.45) },
      '& label.Mui-focused': { color: alpha(NEON, 0.85), fontWeight: 600 },
      '& .MuiFormLabel-asterisk': { color: '#EF4444 !important' },
      '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: alpha(NEON, 0.45), fontSize: 18 },
      '& .MuiSelect-icon': { color: alpha(NEON, 0.45) },
      '& .MuiFormHelperText-root': {
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '0.68rem',
        letterSpacing: 0.3,
        color: alpha(NEON, 0.5),
      },
      '& .MuiFormHelperText-root.Mui-error': { color: '#F87171' },
      '& .MuiOutlinedInput-root .MuiSelect-select': { color: alpha('#E2E8F0', 0.85) },
      ...sx,
    }}
  />
);

// ─── UploadZone ────────────────────────────────────────────────────────────────
const UploadZone = ({ id, label, hint, icon: Icon, isDragging, sx = {} }) => (
  <Paper
    component="label"
    htmlFor={id}
    sx={{
      border: `2px dashed ${alpha(NEON, isDragging ? 0.6 : 0.22)}`,
      borderRadius: '12px',
      p: 3,
      textAlign: 'center',
      cursor: 'pointer',
      background: isDragging ? alpha(NEON, 0.04) : alpha(SURFACE_UP, 0.35),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: IMAGE_PANEL_HEIGHT,
      boxSizing: 'border-box',
      transform: isDragging ? 'scale(1.01)' : 'scale(1)',
      transition: 'all 0.22s ease',
      '&:hover': { borderColor: alpha(NEON, 0.5), background: alpha(NEON, 0.03) },
      ...sx,
    }}
  >
    <Icon sx={{ fontSize: 36, color: alpha(NEON, isDragging ? 0.8 : 0.45), mb: 1, transition: 'color 0.2s ease' }} />
    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '0.85rem', color: alpha('#E2E8F0', 0.65), mb: 0.5 }}>
      {label}
    </Typography>
    {hint && (
      <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', color: alpha(NEON, 0.32) }}>
        {hint}
      </Typography>
    )}
  </Paper>
);

// ─── Divider ───────────────────────────────────────────────────────────────────
const Divider = () => (
  <Box sx={{ height: '1px', background: `linear-gradient(90deg, transparent, ${alpha(NEON, 0.1)}, transparent)` }} />
);

// ─────────────────────────────────────────────────────────────────────────────
const GalleryForm = () => {
  const navigate = useNavigate();
  const { id }   = useParams();
  const { user: currentUser } = useAuth();
  const isEditMode = Boolean(id);

  const [formData, setFormData] = useState({
    event_name: '', event_id: '', publicity: 'public',
    is_achievement: false, achievement_type: '', achievement_description: '',
    representative_picture: null,
    competition_logo: null,
    other_pictures: [],
  });
  const [loading,                 setLoading]                 = useState(false);
  const [submitLoading,           setSubmitLoading]           = useState(false);
  const [error,                   setError]                   = useState(null);
  const [success,                 setSuccess]                 = useState(false);
  const [validationErrors,        setValidationErrors]        = useState({});
  const [showFieldErrors,         setShowFieldErrors]         = useState(false);
  const [representativePreview,   setRepresentativePreview]   = useState(null);
  const [competitionLogoPreview,  setCompetitionLogoPreview]  = useState(null);
  const [additionalPreviews,      setAdditionalPreviews]      = useState([]);
  const [existingOtherPictures,   setExistingOtherPictures]   = useState([]);
  const [existingImagePath,       setExistingImagePath]       = useState(null);
  const [existingCompetitionLogoPath, setExistingCompetitionLogoPath] = useState(null);
  const [representativeRemoved,   setRepresentativeRemoved]   = useState(false);
  const [competitionLogoRemoved,  setCompetitionLogoRemoved]  = useState(false);
  const [events,                  setEvents]                  = useState([]);
  const [dataLoading,             setDataLoading]             = useState(true);
  const [confirmUpdateDialogOpen, setConfirmUpdateDialogOpen] = useState(false);
  const [pendingUpdateRequest,    setPendingUpdateRequest]    = useState(null);
  const [dragTarget,              setDragTarget]              = useState(null);

  const canManageAllGalleries = canManageGallery(currentUser);
  const canCreateGallery      = canManageAllGalleries;

  const { formDraft, submitDraftProps } = useFormDraftBindings({
    formKey: 'gallery',
    userId: currentUser?.id,
    isEditMode,
    enabled: !dataLoading,
    formData,
    setFormData,
  });

  const isFormValid = (() => {
    const hasEvent = Boolean(formData.event_id && formData.event_id !== '__custom__') || Boolean(formData.event_name?.trim());
    const hasPublicity = Boolean(formData.publicity);
    const hasRepresentative = isEditMode
      ? (!representativeRemoved && (Boolean(formData.representative_picture) || Boolean(existingImagePath)))
      : Boolean(formData.representative_picture);
    const hasAchievementFields = formData.is_achievement
      ? Boolean(formData.achievement_type) && Boolean(formData.achievement_description?.trim())
      : true;
    return hasEvent && hasPublicity && hasRepresentative && hasAchievementFields;
  })();

  const publicityOptions = canManageAllGalleries
    ? GALLERY_PUBLICITY_FORM_OPTIONS.map((opt) => {
        const Icon = PUBLICITY_OPTION_ICONS[opt.value];
        return {
          ...opt,
          icon: Icon ? <Icon /> : null,
          color: PUBLICITY_OPTION_COLORS[opt.value] ?? NEON,
        };
      })
    : [];

  const achievementTypes = [
    { value: 'domestic',      label: 'Domestic',      description: 'National level', icon: '🏆' },
    { value: 'international', label: 'International', description: 'Global level',   icon: '🌍' },
  ];

  useEffect(() => {
    const cssId = 'galleryform-ds-css';
    if (!document.getElementById(cssId)) {
      const el = document.createElement('style');
      el.id = cssId; el.innerHTML = GALLERY_CSS;
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    if (!canCreateGallery) { navigate('/dashboard'); return; }
    // Do not reset publicity here — it overwrote the user's choice whenever
    // currentUser/auth re-rendered (common in production), forcing "public".
    fetchEvents();
    if (isEditMode) fetchGalleryItem();
    else setDataLoading(false);
  }, [currentUser, id, isEditMode, canCreateGallery]);

  const fetchEvents = async () => {
    try { const r = await api.get('/events'); setEvents(r.data); }
    catch { setError('Failed to load events.'); }
  };

  const fetchGalleryItem = async () => {
    try {
      setLoading(true);
      const r = await api.get(`/galleries/${id}`);
      const item = r.data;
      setFormData({
        event_name: item.event_name || '',
        event_id: item.event_id ? String(item.event_id) : (item.event_name ? '__custom__' : ''),
        publicity: item.publicity || 'public',
        is_achievement: item.is_achievement || false,
        achievement_type: item.achievement_type || '',
        achievement_description: item.achievement_description || '',
        representative_picture: null,
        competition_logo: null,
        other_pictures: [],
      });
      if (item.representative_picture) {
        setRepresentativePreview(item.representative_picture);
        setExistingImagePath(item.representative_picture);
      }
      if (item.competition_logo) {
        setCompetitionLogoPreview(item.competition_logo);
        setExistingCompetitionLogoPath(item.competition_logo);
      } else {
        setCompetitionLogoPreview(null);
        setExistingCompetitionLogoPath(null);
      }
      const otherPaths = item.other_pictures || [];
      setExistingOtherPictures(otherPaths);
      setAdditionalPreviews(otherPaths);
      setRepresentativeRemoved(false);
      setCompetitionLogoRemoved(false);
    } catch { setError('Failed to load gallery item.'); navigate('/galleries'); }
    finally { setLoading(false); setDataLoading(false); }
  };

  const collectValidationErrors = () => {
    const errors = {};
    const eventId = formData.event_id === '__custom__' ? '' : formData.event_id;
    if (!eventId && !formData.event_name?.trim()) {
      errors.event_name = 'Please select an event or enter a custom name';
    }
    if (!formData.publicity) errors.publicity = 'Visibility is required';
    if (formData.is_achievement) {
      if (!formData.achievement_type) errors.achievement_type = 'Achievement type is required';
      if (!formData.achievement_description?.trim()) {
        errors.achievement_description = 'Achievement description is required';
      }
    }
    const hasCover = Boolean(formData.representative_picture)
      || (existingImagePath && !representativeRemoved);
    if (!hasCover) errors.representative_picture = 'Representative picture is required';
    return errors;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleEventSelect = (val) => {
    if (val === '__custom__') {
      setFormData(prev => ({
        ...prev,
        event_id: '__custom__',
        event_name: prev.event_id === '__custom__' ? prev.event_name : '',
      }));
    } else {
      setFormData(prev => ({ ...prev, event_id: val, event_name: '' }));
    }
    if (error) setError(null);
    if (showFieldErrors && validationErrors.event_name) {
      setValidationErrors(prev => ({ ...prev, event_name: '' }));
    }
  };

  const handlePublicitySelect = (value) => {
    setFormData(prev => ({ ...prev, publicity: value }));
    if (error) setError(null);
    if (showFieldErrors && validationErrors.publicity) {
      setValidationErrors(prev => ({ ...prev, publicity: '' }));
    }
  };

  const toggleAchievement = () => {
    setFormData((prev) => {
      const next = !prev.is_achievement;
      if (!next) {
        setCompetitionLogoPreview(null);
        setExistingCompetitionLogoPath(null);
        setCompetitionLogoRemoved(false);
        return {
          ...prev,
          is_achievement: false,
          achievement_type: '',
          achievement_description: '',
          competition_logo: null,
        };
      }
      return { ...prev, is_achievement: true };
    });
  };

  const validateImageFiles = (files, { allowMultiple = true } = {}) => {
    if (!Array.isArray(files) || files.length === 0) return [];
    if (!allowMultiple && files.length > 1) { setError('Please upload only one representative image.'); return []; }
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { setError('Each image must be less than 10 MB'); return []; }
      if (!f.type.startsWith('image/')) { setError('Please select only image files'); return []; }
    }
    return files;
  };

  const applyRepresentativeFile = (file) => {
    if (!file) return;
    setRepresentativeRemoved(false);
    setFormData(prev => ({ ...prev, representative_picture: file }));
    const reader = new FileReader();
    reader.onload = (e) => setRepresentativePreview(e.target.result);
    reader.readAsDataURL(file);
    setError(null);
    if (showFieldErrors && validationErrors.representative_picture) {
      setValidationErrors(prev => ({ ...prev, representative_picture: '' }));
    }
  };

  const applyCompetitionLogoFile = (file) => {
    if (!file) return;
    setCompetitionLogoRemoved(false);
    setFormData((prev) => ({ ...prev, competition_logo: file }));
    const reader = new FileReader();
    reader.onload = (e) => setCompetitionLogoPreview(e.target.result);
    reader.readAsDataURL(file);
    setError(null);
  };

  const applyAdditionalFiles = (files) => {
    if (!files.length) return;
    setFormData(prev => ({ ...prev, other_pictures: [...prev.other_pictures, ...files] }));
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => setAdditionalPreviews(prev => [...prev, e.target.result]);
      reader.readAsDataURL(file);
    });
    setError(null);
  };

  const handleRepresentativeImageChange = (e) => {
    const files = validateImageFiles(Array.from(e.target.files || []), { allowMultiple: false });
    if (files.length) applyRepresentativeFile(files[0]);
  };

  const handleCompetitionLogoChange = (e) => {
    const files = validateImageFiles(Array.from(e.target.files || []), { allowMultiple: false });
    if (files.length) applyCompetitionLogoFile(files[0]);
  };

  const handleAdditionalImagesChange = (e) => {
    const files = validateImageFiles(Array.from(e.target.files || []), { allowMultiple: true });
    if (files.length) applyAdditionalFiles(files);
  };

  const handleDragOver  = zone => e => { e.preventDefault(); e.stopPropagation(); if (dragTarget !== zone) setDragTarget(zone); };
  const handleDragLeave = zone => e => { e.preventDefault(); e.stopPropagation(); if (dragTarget === zone) setDragTarget(null); };
  const handleDrop      = zone => e => {
    e.preventDefault(); e.stopPropagation(); setDragTarget(null);
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (!dropped.length) return;
    if (zone === 'representative') {
      const files = validateImageFiles(dropped, { allowMultiple: false });
      if (files.length) applyRepresentativeFile(files[0]);
    } else if (zone === 'competition_logo') {
      const files = validateImageFiles(dropped, { allowMultiple: false });
      if (files.length) applyCompetitionLogoFile(files[0]);
    } else {
      const files = validateImageFiles(dropped);
      if (files.length) applyAdditionalFiles(files);
    }
  };

  const removeRepresentativeImage = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    const hadExisting = Boolean(existingImagePath);
    setFormData((prev) => ({ ...prev, representative_picture: null }));
    setRepresentativePreview(null);
    setRepresentativeRemoved(isEditMode && hadExisting);
  };

  const removeCompetitionLogo = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    const hadExisting = Boolean(existingCompetitionLogoPath);
    setFormData((prev) => ({ ...prev, competition_logo: null }));
    setCompetitionLogoPreview(null);
    setCompetitionLogoRemoved(isEditMode && hadExisting);
  };

  const appendOtherPicturesForEdit = (submitData) => {
    submitData.append('sync_other_pictures', '1');
    existingOtherPictures.forEach((p, i) => submitData.append(`retained_other_pictures[${i}]`, p));
    formData.other_pictures.forEach((f, i) => submitData.append(`other_pictures[${i}]`, f));
  };

  const removeAdditionalImage = (index, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    const existingCount = existingOtherPictures.length;
    if (index < existingCount) setExistingOtherPictures(prev => prev.filter((_, i) => i !== index));
    else {
      const ni = index - existingCount;
      setFormData(prev => ({ ...prev, other_pictures: prev.other_pictures.filter((_, i) => i !== ni) }));
    }
    setAdditionalPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const resolvedEventId = formData.event_id === '__custom__' ? '' : formData.event_id;

  const buildSubmitData = () => {
    const sd = new FormData();
    if (resolvedEventId) sd.append('event_id', resolvedEventId);
    else sd.append('event_name', formData.event_name.trim());
    sd.append('publicity', formData.publicity);
    sd.append('is_achievement', formData.is_achievement ? '1' : '0');
    if (formData.is_achievement) {
      sd.append('achievement_type', formData.achievement_type);
      sd.append('achievement_description', formData.achievement_description.trim());
    }
    if (formData.representative_picture) sd.append('representative_picture', formData.representative_picture);
    if (representativeRemoved) sd.append('remove_representative_picture', '1');
    if (formData.is_achievement && formData.competition_logo) {
      sd.append('competition_logo', formData.competition_logo);
    }
    if (competitionLogoRemoved) sd.append('remove_competition_logo', '1');
    return sd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitLoading(true);

      if (isEditMode) {
        setError(null);
        setValidationErrors({});
        setShowFieldErrors(false);
        const submitData = buildSubmitData();
        appendOtherPicturesForEdit(submitData);
        setPendingUpdateRequest({ url: `/galleries/${id}`, submitData });
        setConfirmUpdateDialogOpen(true);
        setSubmitLoading(false);
        return;
      }

      setError(null);
      const errors = collectValidationErrors();
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        setShowFieldErrors(true);
        setError(formatFieldErrorsMessage(errors));
        setSubmitLoading(false);
        return;
      }
      setShowFieldErrors(false);

      const submitData = buildSubmitData();
      formData.other_pictures.forEach((f, i) => submitData.append(`other_pictures[${i}]`, f));
      const response = await api.post('/galleries', submitData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (response.data) {
        if (!isEditMode) formDraft.clearDraft();
        setSuccess(true);
        setTimeout(() => navigate('/galleries', { state: { refreshList: Date.now() } }), 1200);
      }
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      if (apiErrors) {
        const mapped = {};
        Object.entries(apiErrors).forEach(([key, msgs]) => {
          const msg = Array.isArray(msgs) ? msgs.find(Boolean) : msgs;
          if (msg) mapped[key] = String(msg);
        });
        if (Object.keys(mapped).length) {
          setValidationErrors(mapped);
          setShowFieldErrors(true);
          setError(formatFieldErrorsMessage(mapped));
        } else {
          setError(err.response?.data?.message || 'Failed to save gallery item. Please try again.');
        }
      } else {
        setError(err.response?.data?.message || 'Failed to save gallery item. Please try again.');
      }
    } finally { setSubmitLoading(false); }
  };

  const confirmUpdate = async () => {
    const errors = collectValidationErrors();
    if (Object.keys(errors).length > 0) {
      rejectConfirmValidation({
        errors,
        setValidationErrors,
        setError,
        setConfirmUpdateDialogOpen,
        setPendingUpdateRequest,
        setShowFieldErrors,
      });
      return;
    }
    try {
      setSubmitLoading(true);
      await api.post(pendingUpdateRequest.url, pendingUpdateRequest.submitData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess(true);
      setTimeout(() => navigate('/galleries', { state: { refreshList: Date.now() } }), 1200);
    } catch (err) {
      const apiErrors = err.response?.data?.errors;
      if (apiErrors) {
        const mapped = {};
        Object.entries(apiErrors).forEach(([key, msgs]) => {
          const msg = Array.isArray(msgs) ? msgs.find(Boolean) : msgs;
          if (msg) mapped[key] = String(msg);
        });
        if (Object.keys(mapped).length) {
          setValidationErrors(mapped);
          setShowFieldErrors(true);
          setError(formatFieldErrorsMessage(mapped));
        } else {
          setError(err.response?.data?.message || 'Failed to update gallery item. Please try again.');
        }
      } else {
        setError(err.response?.data?.message || 'Failed to update gallery item. Please try again.');
      }
    } finally { setSubmitLoading(false); setConfirmUpdateDialogOpen(false); setPendingUpdateRequest(null); }
  };

  const selectedEvent = events.find(ev => String(ev.id) === String(formData.event_id));

  const eventSelectOptions = useMemo(() => [
    ...events.map(ev => ({
      value: String(ev.id),
      label: ev.start_date
        ? `${ev.name} · ${new Date(ev.start_date).toLocaleDateString()}`
        : ev.name,
    })),
    { value: '__custom__', label: 'Enter a custom name…' },
  ], [events]);

  const achievementSelectOptions = useMemo(
    () => achievementTypes.map(t => ({ value: t.value, label: `${t.icon} ${t.label}` })),
    []
  );

  // ── Guards ────────────────────────────────────────────────────────────────────
  if (dataLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <Loading message={isEditMode ? 'Loading gallery item' : 'Loading form'} size={56} variant="primary" />
    </Box>
  );
  if (!currentUser) return <Container maxWidth="lg" sx={{ mt: 4 }}><FeedbackMessage message="Please log in to access this page." severity="error" /></Container>;
  if (!canCreateGallery) return <Container maxWidth="lg" sx={{ mt: 4 }}><FeedbackMessage message="Only team leaders and multimedia members can create galleries." severity="error" /></Container>;

  const fieldHelper = (name) => (
    showFieldErrors && validationErrors[name] ? validationErrors[name] : undefined
  );
  const fieldHasError = (name) => showFieldErrors && !!validationErrors[name];

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

      <Header
        title={isEditMode ? 'Edit Gallery Item' : 'Create New Gallery Item'}
        subtitle={isEditMode ? 'Update gallery item information and images' : 'Upload a new image to the gallery with details'}
        icon={GalleryIcon}
        showBackButton
        onBackClick={() => navigate('/galleries')}
        backButtonText="Back to Gallery"
      />

      <FeedbackMessage
        message={success ? 'Gallery saved successfully!' : ''}
        severity="success"
        onClose={() => setSuccess(false)}
        sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(NEON3, 0.12), border: `1px solid ${alpha(NEON3, 0.35)}`, color: alpha(NEON3, 0.95) }}
      />
      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5' }}
      />

      {formDraft.showNotice && (
        <FormDraftNotice
          savedAt={formDraft.restoredAt}
          onDiscard={formDraft.discardDraft}
          onDismiss={formDraft.dismissNotice}
        />
      )}

      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

          {/* ══ Section 1 — Event ══════════════════════════════════════════════ */}
          <SectionCard title="Event" icon={<EventIcon />} delay={0}>

            <SearchableSelect
              label="Select Event"
              value={formData.event_id}
              onChange={handleEventSelect}
              options={eventSelectOptions}
              required
              startIcon={<EventIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
            />

            <AnimatePresence>
              {selectedEvent && (
                <MotionBox
                  key="badge"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18 }}
                  sx={{ mt: 1.25 }}
                >
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.6, borderRadius: '8px', background: alpha(NEON, 0.06), border: `1px solid ${alpha(NEON, 0.16)}` }}>
                    <CheckCircleIcon sx={{ fontSize: 13, color: NEON3 }} />
                    <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.8rem', color: alpha('#E2E8F0', 0.75) }}>
                      <Box component="span" sx={{ fontWeight: 700, color: NEON }}>{selectedEvent.name}</Box>
                      {selectedEvent.start_date && (
                        <Box component="span" sx={{ color: alpha(NEON, 0.45), ml: 1 }}>
                          {new Date(selectedEvent.start_date).toLocaleDateString()}
                        </Box>
                      )}
                    </Typography>
                  </Box>
                </MotionBox>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {formData.event_id === '__custom__' && (
                <motion.div
                  key="custom-name"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.22, ease: 'easeInOut' }}
                  style={{ overflow: 'hidden' }}
                >
                  <Box sx={{ mt: 1.5 }}>
                    <StyledField
                      fullWidth
                      required
                      label="Custom Event Name"
                      name="event_name"
                      value={formData.event_name}
                      onChange={handleChange}
                      autoFocus
                      error={fieldHasError('event_name')}
                      helperText={fieldHelper('event_name') || 'This name will be saved with the gallery item'}
                      InputProps={{ startAdornment: <InputAdornment position="start"><DescriptionIcon /></InputAdornment> }}
                    />
                  </Box>
                </motion.div>
              )}
            </AnimatePresence>

          </SectionCard>

          {/* ══ Section 2 — Visibility ════════════════════════════════════════ */}
          <SectionCard title="Visibility" icon={<PublicIcon />} delay={0.06}>
            <Box>
              <Typography sx={labelSx()}>
                Publicity <Box component="span" sx={{ color: '#EF4444' }}>*</Box>
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1 }}>
                {publicityOptions.map(opt => {
                  const isSelected = formData.publicity === opt.value;
                  return (
                    <Button
                      key={opt.value}
                      type="button"
                      onClick={() => handlePublicitySelect(opt.value)}
                      sx={{
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        borderRadius: '10px',
                        px: 1.5, py: 1.25,
                        minHeight: 72,
                        border: `1px solid ${alpha(opt.color, isSelected ? 0.55 : 0.2)}`,
                        background: isSelected
                          ? `linear-gradient(135deg, ${alpha(opt.color, 0.14)}, ${alpha(SURFACE_UP, 0.75)})`
                          : alpha(SURFACE_UP, 0.35),
                        color: alpha('#E2E8F0', 0.9),
                        transition: 'all 0.18s ease',
                        '&:hover': {
                          borderColor: alpha(opt.color, 0.45),
                          background: `linear-gradient(135deg, ${alpha(opt.color, 0.09)}, ${alpha(SURFACE_UP, 0.65)})`,
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
                        <Box sx={{ color: opt.color, mt: 0.1, flexShrink: 0 }}>{opt.icon}</Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '0.82rem', color: alpha('#E2E8F0', 0.9), lineHeight: 1.2 }}>
                            {opt.label}
                          </Typography>
                          <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.68rem', color: alpha(opt.color, 0.6), lineHeight: 1.4, mt: 0.35, whiteSpace: 'normal' }}>
                            {opt.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Button>
                  );
                })}
              </Box>
            </Box>
          </SectionCard>

          {/* ══ Section 3 — Achievement Details (unified) ════════════════════ */}
          <SectionCard title="Achievement" icon={<AchievementIcon />} delay={0.09}>

            {/* Toggle row */}
            <Box
              role="button"
              tabIndex={0}
              onClick={toggleAchievement}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleAchievement();
                }
              }}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                px: 2, py: 1.5, borderRadius: '12px',
                background: formData.is_achievement
                  ? `linear-gradient(135deg, ${alpha(NEON3, 0.1)}, ${alpha(SURFACE_UP, 0.8)})`
                  : alpha(SURFACE_UP, 0.4),
                border: `1px solid ${alpha(NEON3, formData.is_achievement ? 0.35 : 0.12)}`,
                transition: 'all 0.25s ease',
                cursor: 'pointer',
                '&:hover': {
                  background: formData.is_achievement
                    ? `linear-gradient(135deg, ${alpha(NEON3, 0.14)}, ${alpha(SURFACE_UP, 0.85)})`
                    : alpha(SURFACE_UP, 0.6),
                  borderColor: alpha(NEON3, formData.is_achievement ? 0.45 : 0.22),
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: '10px',
                  background: alpha(NEON3, formData.is_achievement ? 0.18 : 0.07),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.22s ease',
                  flexShrink: 0,
                }}>
                  <AchievementIcon sx={{ color: alpha(NEON3, formData.is_achievement ? 1 : 0.38), fontSize: 18, transition: 'color 0.2s ease' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '0.9rem', color: alpha('#E2E8F0', 0.9), lineHeight: 1.2 }}>
                    Mark as Achievement
                  </Typography>
                  <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', color: alpha(NEON3, 0.5), mt: 0.25 }}>
                    Highlight a competition result or accomplishment
                  </Typography>
                </Box>
              </Box>
              <Switch
                checked={formData.is_achievement}
                readOnly
                tabIndex={-1}
                name="is_achievement"
                sx={{
                  flexShrink: 0,
                  pointerEvents: 'none',
                  '& .MuiSwitch-switchBase.Mui-checked': { color: NEON3 },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: alpha(NEON3, 0.45) },
                  '& .MuiSwitch-track': { backgroundColor: alpha(NEON, 0.18) },
                }}
              />
            </Box>

            {/* Expanded detail area — type, description, competition logo */}
            <AnimatePresence>
              {formData.is_achievement && (
                <motion.div
                  key="achievement-expanded"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.28, ease: 'easeInOut' }}
                  style={{ overflow: 'hidden' }}
                >
                  <Box sx={{ mt: 2 }}>

                    {/* Accent divider */}
                    <Box sx={{
                      height: '1px', mb: 2,
                      background: `linear-gradient(90deg, ${alpha(NEON3, 0.4)}, ${alpha(NEON3, 0.06)}, transparent)`,
                    }} />

                    {/* ── Two-column layout: fields left, logo right ── */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>

                      {/* LEFT — Type + Description */}
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ flex: '0 0 auto' }}>
                          <SearchableSelect
                            required
                            label="Achievement Type"
                            value={formData.achievement_type}
                            onChange={(v) => {
                              setFormData(prev => ({ ...prev, achievement_type: v }));
                              if (error) setError(null);
                              if (showFieldErrors && validationErrors.achievement_type) {
                                setValidationErrors(prev => ({ ...prev, achievement_type: '' }));
                              }
                            }}
                            options={achievementSelectOptions}
                            error={fieldHasError('achievement_type')}
                            helperText={fieldHelper('achievement_type')}
                            startIcon={<StarIcon sx={{ color: alpha(NEON, 0.45), fontSize: 18 }} />}
                          />
                        </Box>
                        <Box sx={{ flex: 1, mt: 1 }}>
                          <StyledField
                            fullWidth required
                            label="Achievement Description"
                            name="achievement_description"
                            value={formData.achievement_description}
                            onChange={handleChange}
                            multiline rows={4}
                            error={fieldHasError('achievement_description')}
                            helperText={fieldHelper('achievement_description')}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                  <DescriptionIcon />
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Vertical separator (desktop only) */}
                      <Box sx={{
                        display: { xs: 'none', md: 'block' },
                        width: '1px',
                        background: `linear-gradient(180deg, transparent, ${alpha(NEON3, 0.15)}, transparent)`,
                        flexShrink: 0,
                      }} />

                      {/* RIGHT — Competition Logo */}
                      <Box sx={{ flex: '0 0 260px', display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {/* Label row */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography sx={labelSx(alpha(NEON3, 0.55))}>
                            Competition Logo
                          </Typography>
                          <Box sx={{
                            ml: 0.5, px: 0.85, py: 0.2, borderRadius: '4px',
                            background: alpha(NEON3, 0.08),
                            border: `1px solid ${alpha(NEON3, 0.18)}`,
                          }}>
                          </Box>
                        </Box>

                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleCompetitionLogoChange}
                          style={{ display: 'none' }}
                          id="competition-logo-upload"
                        />

                        {competitionLogoPreview ? (
                          <MotionPaper
                            initial={{ opacity: 0, scale: 0.97 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.2 }}
                            onDragOver={handleDragOver('competition_logo')}
                            onDragLeave={handleDragLeave('competition_logo')}
                            onDrop={handleDrop('competition_logo')}
                            sx={{
                              position: 'relative', borderRadius: '10px', overflow: 'hidden',
                              background: alpha(SURFACE_UP, 0.6),
                              border: `1px solid ${alpha(NEON3, dragTarget === 'competition_logo' ? 0.5 : 0.18)}`,
                              transition: 'border-color 0.2s ease',
                              flex: 1,
                            }}
                          >
                            <StorageImage
                              storagePath={competitionLogoPreview}
                              alt="Competition logo preview"
                              sx={{
                                width: '100%', height: 'auto', display: 'block',
                                maxHeight: 160, objectFit: 'contain',
                                bgcolor: alpha('#fff', 0.03), p: 1.5,
                              }}
                            />
                            <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.75 }}>
                              <Tooltip title="Change logo">
                                <label htmlFor="competition-logo-upload">
                                  <IconButton component="span" size="small" sx={{ background: alpha(NEON3, 0.15), color: NEON3, '&:hover': { background: alpha(NEON3, 0.28) } }}>
                                    <UploadIcon sx={{ fontSize: 15 }} />
                                  </IconButton>
                                </label>
                              </Tooltip>
                              <Tooltip title="Remove">
                                <IconButton type="button" onClick={removeCompetitionLogo} size="small" sx={{ background: alpha('#EF4444', 0.15), color: '#F87171', '&:hover': { background: alpha('#EF4444', 0.28) } }}>
                                  <DeleteIcon sx={{ fontSize: 15 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </MotionPaper>
                        ) : (
                          <Box
                            onDragOver={handleDragOver('competition_logo')}
                            onDragLeave={handleDragLeave('competition_logo')}
                            onDrop={handleDrop('competition_logo')}
                            sx={{ flex: 1, minHeight: 0, display: 'flex' }}
                          >
                            <UploadZone
                              id="competition-logo-upload"
                              label="Upload competition logo"
                              hint="JPEG · PNG · WebP · max 10 MB"
                              icon={AchievementIcon}
                              isDragging={dragTarget === 'competition_logo'}
                              sx={{ width: '100%', height: '100%', minHeight: 0 }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>

                  </Box>
                </motion.div>
              )}
            </AnimatePresence>

          </SectionCard>

          {/* ══ Section 4 — Images ════════════════════════════════════════════ */}
          <SectionCard title="Images" icon={<ImageIcon />} delay={0.12}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5 }}>

              {/* Representative picture */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={labelSx()}>
                  Cover Photo <Box component="span" sx={{ color: '#EF4444' }}>*</Box>
                </Typography>
                <input type="file" accept="image/*" onChange={handleRepresentativeImageChange} style={{ display: 'none' }} id="representative-image-upload" />

                {representativePreview ? (
                  <MotionPaper
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                    onDragOver={handleDragOver('representative')}
                    onDragLeave={handleDragLeave('representative')}
                    onDrop={handleDrop('representative')}
                    sx={{
                      position: 'relative', borderRadius: '12px', overflow: 'hidden',
                      height: IMAGE_PANEL_HEIGHT,
                      background: alpha(SURFACE_UP, 0.6),
                      border: `1px solid ${alpha(NEON, dragTarget === 'representative' ? 0.5 : 0.18)}`,
                      transition: 'border-color 0.2s ease',
                    }}
                  >
                    <StorageImage storagePath={representativePreview} alt="Cover preview"
                      sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }} />
                    <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.75 }}>
                      <Tooltip title="Change image">
                        <label htmlFor="representative-image-upload">
                          <IconButton component="span" size="small" sx={{ background: alpha(NEON, 0.15), color: NEON, '&:hover': { background: alpha(NEON, 0.28) } }}>
                            <UploadIcon sx={{ fontSize: 15 }} />
                          </IconButton>
                        </label>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton type="button" onClick={removeRepresentativeImage} size="small" sx={{ background: alpha('#EF4444', 0.15), color: '#F87171', '&:hover': { background: alpha('#EF4444', 0.28) } }}>
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </MotionPaper>
                ) : (
                  <Box onDragOver={handleDragOver('representative')} onDragLeave={handleDragLeave('representative')} onDrop={handleDrop('representative')}>
                    <UploadZone
                      id="representative-image-upload"
                      label="Upload cover photo"
                      hint="JPEG · PNG · WebP · max 10 MB"
                      icon={AddPhotoIcon}
                      isDragging={dragTarget === 'representative'}
                    />
                  </Box>
                )}

                {fieldHelper('representative_picture') && (
                  <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.68rem', color: '#F87171', mt: 0.75, ml: 0.5 }}>
                    {fieldHelper('representative_picture')}
                  </Typography>
                )}
              </Box>

              {/* Additional pictures */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={labelSx()}>
                  Additional Photos
                  {additionalPreviews.length > 0 && (
                    <Box component="span" sx={{ ml: 1, color: alpha(NEON, 0.4), fontWeight: 400, textTransform: 'none', fontSize: '0.7rem', letterSpacing: 0 }}>
                      {additionalPreviews.length} added
                    </Box>
                  )}
                </Typography>
                <input type="file" accept="image/*" multiple onChange={handleAdditionalImagesChange} style={{ display: 'none' }} id="additional-images-upload" />

                {additionalPreviews.length > 0 ? (
                  <Box
                    onDragOver={handleDragOver('additional')}
                    onDragLeave={handleDragLeave('additional')}
                    onDrop={handleDrop('additional')}
                    sx={{
                      display: 'flex', flexDirection: 'column', gap: 1.25,
                      height: IMAGE_PANEL_HEIGHT,
                      overflowY: 'auto',
                      borderRadius: '10px',
                      border: dragTarget === 'additional' ? `1px solid ${alpha(NEON, 0.3)}` : '1px solid transparent',
                      p: dragTarget === 'additional' ? 0.75 : 0,
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: 1 }}>
                      {additionalPreviews.map((preview, index) => (
                        <MotionBox
                          key={index}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.18 }}
                          sx={{ position: 'relative', borderRadius: '8px', overflow: 'hidden', aspectRatio: '1', background: alpha(SURFACE_UP, 0.6), border: `1px solid ${alpha(NEON, 0.15)}` }}
                        >
                          <StorageImage storagePath={preview} alt={`Photo ${index + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          <Tooltip title="Remove">
                            <IconButton
                              type="button"
                              onClick={(e) => removeAdditionalImage(index, e)}
                              size="small"
                              sx={{ position: 'absolute', top: 3, right: 3, background: alpha('#EF4444', 0.75), color: '#fff', width: 20, height: 20, '&:hover': { background: '#EF4444' } }}
                            >
                              <CloseIcon sx={{ fontSize: 11 }} />
                            </IconButton>
                          </Tooltip>
                        </MotionBox>
                      ))}
                    </Box>

                    <label htmlFor="additional-images-upload">
                      <Box sx={{
                        border: `2px dashed ${alpha(NEON, 0.2)}`, borderRadius: '8px', p: 1.25,
                        textAlign: 'center', cursor: 'pointer',
                        background: alpha(SURFACE_UP, 0.3),
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                        transition: 'all 0.2s ease',
                        '&:hover': { borderColor: alpha(NEON, 0.45), background: alpha(NEON, 0.03) },
                      }}>
                        <AddPhotoIcon sx={{ fontSize: 16, color: alpha(NEON, 0.5) }} />
                        <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: alpha('#E2E8F0', 0.55) }}>
                          Add more photos
                        </Typography>
                      </Box>
                    </label>
                  </Box>
                ) : (
                  <Box onDragOver={handleDragOver('additional')} onDragLeave={handleDragLeave('additional')} onDrop={handleDrop('additional')}>
                    <UploadZone
                      id="additional-images-upload"
                      label="Upload additional photos"
                      hint="Optional · multiple files supported"
                      icon={AddPhotoIcon}
                      isDragging={dragTarget === 'additional'}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </SectionCard>

          {/* ══ Submit ════════════════════════════════════════════════════════ */}
          <SubmitActionsCard
            statusText={isEditMode ? 'A confirmation prompt will appear before saving.' : 'Complete the form to submit a new gallery item.'}
            missingRequiredFields={isEditMode ? null : [!(formData.event_id && formData.event_id !== '__custom__') && !formData.event_name?.trim() && 'Event', !formData.publicity && 'Publicity', !formData.representative_picture && 'Representative Picture', formData.is_achievement && !formData.achievement_type && 'Achievement Type', formData.is_achievement && !formData.achievement_description?.trim() && 'Achievement Description'].filter(Boolean)}
            readyStatusText="All required fields are complete. Ready to submit gallery item."
            cancelLabel="Cancel"
            onCancel={() => navigate('/galleries')}
            submitLabel={isEditMode ? 'Update Gallery' : 'Create Gallery'}
            submitDisabled={submitLoading || !isFormValid}
            loading={submitLoading}
            submitStartIcon={<SaveIcon />}
            {...submitDraftProps}
          />
        </Box>
      </form>

      <ConfirmUpdateDialog
        open={confirmUpdateDialogOpen}
        onClose={() => { setConfirmUpdateDialogOpen(false); setPendingUpdateRequest(null); }}
        variant="edit"
        itemType="gallery item"
        itemName={formData.event_name || selectedEvent?.name || 'Gallery Item'}
        onConfirm={confirmUpdate}
        title="Confirm Update"
        description="This action will update the gallery item and cannot be undone."
        confirmLabel="Update Gallery"
        loading={submitLoading}
        confirmDisabled={!isFormValid}
      />
    </Container>
  );
};

export default GalleryForm;
